import { pairHasOpenIssue, useTaskActivitiesForServiceRequest } from '@commenda/api/tasks';
import { Button, Icons, useToggle } from '@commenda/kakashi';
import {
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { ServiceRequestTaskLabel } from '../../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { updateTaskStatus } from '../../../utils';
import { USCCORPIncorporationTasks } from '../config';
import { DocSignModal } from './DocSignModal';

export function ClientUploadsSignedDocs({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) {
  const { role, mutate, serviceRequest } = context;

  const clientUploadSignedDocsModal = useToggle();

  const verifyDocumentsTask = serviceRequest?.taskGroups
    ?.find((srtg) => srtg.id === task.taskGroupId)
    ?.tasks?.find(
      (srt) =>
        srt.legacyServiceRequestTaskTaskId ===
        USCCORPIncorporationTasks.VERIFY_SIGNED_DOCUMENTS
    );
  const { data: activityFeed } = useTaskActivitiesForServiceRequest(
    serviceRequest?.id
  );
  const taskHasOpenIssue = pairHasOpenIssue(
    activityFeed?.activities,
    task,
    verifyDocumentsTask
  );

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      if (
        taskHasOpenIssue &&
        task.status !== TaskStatus.COMPLETED &&
        state.role === TaskParty.CLIENT
      ) {
        return {
          actions: (
            <Button
              size="sm"
              variant="secondary"
              onClick={clientUploadSignedDocsModal.show}
            >
              Fix issues
              <Icons.ArrowRight className="size-4" />
            </Button>
          ),
          label: (
            <ServiceRequestTaskLabel
              status={task.status}
              label="Upload signed shareholding documents"
              timestamp={task.updatedAt}
            />
          ),
        };
      }

      const stateMap = {
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Client uploads signed shareholding documents" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="Client uploads signed shareholding documents"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={clientUploadSignedDocsModal.show}
                variant="primary"
              >
                Start
                <Icons.ArrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload signed shareholding documents" />
            ),
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={clientUploadSignedDocsModal.show}
              >
                Continue
                <Icons.ArrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Uploading signed shareholding documents"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Upload signed shareholding documents" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button
                size="sm"
                onClick={clientUploadSignedDocsModal.show}
                variant="secondary"
              >
                View documents
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                status={task.status}
                label="Upload signed shareholding documents"
                timestamp={task.updatedAt}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <DocSignModal
        serviceRequest={serviceRequest}
        mutate={mutate}
        open={clientUploadSignedDocsModal.visible}
        onClose={clientUploadSignedDocsModal.hide}
        onMarkTaskAsComplete={async () => {
          await updateTaskStatus({
            task,
            mutate,
            status: TaskStatus.COMPLETED,
          });
          clientUploadSignedDocsModal.toggle();
        }}
        task={task}
        serviceRequestId={serviceRequest?.id}
      />
    </div>
  );
}
